import { Component, Inject, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NUDGER_PROVISION_LABELS, NudgerRule, NudgerRuleField, NudgerRuleHistoryEntry } from '../../_models/admin/nudger-rule.models';
import { NudgerRulesService } from '../../_services/nudger-rules/nudger-rules.service';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';

export interface NudgerRuleHistoryData {
  rule: NudgerRule;
}

/** One line of a history entry: what changed, in the form's own words. */
export interface NudgerRuleChange {
  label: string;
  before: string;
  after: string;
}

/**
 * The change history of one nudger rule (latest 50, newest first), as a right-hand drawer. Each entry is described
 * field by field with the form's labels, not as the stored JSON.
 */
@Component({
  selector: 'nudger-rule-history-drawer',
  standalone: true,
  imports: [DatePipe, AdminEmailComponent],
  templateUrl: 'nudger-rule-history-drawer.component.html'
})
export class NudgerRuleHistoryDrawerComponent implements OnInit {

  public rows: NudgerRuleHistoryEntry[] = [];
  public loading = false;
  public error: string = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NudgerRuleHistoryData,
    private dialogRef: MatDialogRef<NudgerRuleHistoryDrawerComponent>,
    private service: NudgerRulesService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.service.getHistory(this.data.rule.workFlowType).subscribe({
      next: rows => {
        this.loading = false;
        this.rows = rows || [];
      },
      error: error => {
        this.loading = false;
        this.error = (error && error.message) || 'Could not load the history.';
      }
    });
  }

  /** Every field (and the on/off switch) that differs between before and after. */
  public changes(row: NudgerRuleHistoryEntry): NudgerRuleChange[] {
    const result: NudgerRuleChange[] = [];
    if (row.beforeEnabled !== row.afterEnabled) {
      result.push({ label: 'Rule', before: this.onOff(row.beforeEnabled), after: this.onOff(row.afterEnabled) });
    }

    const before = this.parse(row.beforeParameters);
    const after = this.parse(row.afterParameters);
    (this.data.rule.fields || []).forEach(field => {
      const b = this.describe(field, this.read(before, field));
      const a = this.describe(field, this.read(after, field));
      if (a !== b) {
        result.push({ label: field.label, before: b, after: a });
      }
    });
    return result;
  }

  public close(): void {
    this.dialogRef.close();
  }

  private onOff(value: boolean | null): string {
    return value === null || value === undefined ? 'not set' : (value ? 'On' : 'Off');
  }

  private parse(json: string | null): { [key: string]: any } | null {
    if (!json) {
      return null;
    }
    try {
      const value = JSON.parse(json);
      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    } catch {
      return null;
    }
  }

  // The stored keys are read case-insensitively by the API, so match them the same way here.
  private read(json: { [key: string]: any } | null, field: NudgerRuleField): any {
    if (!json) {
      return null;
    }
    const key = Object.keys(json).find(k => k.toLowerCase() === (field.jsonName || '').toLowerCase());
    return key === undefined ? null : json[key];
  }

  private describe(field: NudgerRuleField, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'not set';
    }
    if (field.kind === 'time') {
      const match = /^(\d{1,2}):(\d{2})/.exec(String(value));
      return match ? match[1].padStart(2, '0') + ':' + match[2] : String(value);
    }
    if (field.kind === 'provision') {
      return NUDGER_PROVISION_LABELS[String(value)] || String(value);
    }
    return field.unit ? value + ' ' + field.unit : String(value);
  }
}
